import { createContext, useContext, useEffect, useMemo, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { THEMES, THEME_BY_ID, DEFAULT_THEME } from "./themes";
import api from "../services/api";

const STORAGE_KEY = "aura-theme";

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  themes: THEMES,
  available: THEMES,
  disabledThemeIds: [],
  toggleThemeAvailability: async () => {},
});

export function ThemeProvider({ children }) {
  const user = useSelector((state) => state.auth.user);
  const packageLevel = user?.packageLevel;
  const role = user?.role;

  const [disabledThemeIds, setDisabledThemeIds] = useState([]);

  const fetchDisabledThemes = useCallback(async () => {
    try {
      const res = await api.get("/settings/value", { params: { key: "disabled_themes", defaultValue: "" } });
      const val = res.data?.data || "";
      setDisabledThemeIds(val ? val.split(",") : []);
    } catch (err) {
      console.debug("Failed to fetch disabled themes", err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchDisabledThemes();
    }
  }, [user, fetchDisabledThemes]);

  const toggleThemeAvailability = useCallback(async (themeId) => {
    if (role !== "ADMIN") return false;
    let nextList;
    if (disabledThemeIds.includes(themeId)) {
      nextList = disabledThemeIds.filter(id => id !== themeId);
    } else {
      nextList = [...disabledThemeIds, themeId];
    }
    try {
      await api.put("/settings/value", null, {
        params: {
          key: "disabled_themes",
          value: nextList.join(",")
        }
      });
      setDisabledThemeIds(nextList);
      return true;
    } catch (err) {
      console.error("Failed to toggle theme", err);
      return false;
    }
  }, [disabledThemeIds, role]);

  const available = useMemo(() => {
    let baseThemes = THEMES;
    // Non-admin users cannot see or select disabled themes
    if (role !== "ADMIN") {
      baseThemes = THEMES.filter(t => !disabledThemeIds.includes(t.id));
    }
    if (role === "ADMIN") return THEMES;
    return baseThemes.filter((t) => (packageLevel ?? 1) >= t.minLevel);
  }, [packageLevel, role, disabledThemeIds]);

  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && THEME_BY_ID[stored] ? stored : DEFAULT_THEME;
  });

  useEffect(() => {
    if (!available.some((t) => t.id === theme)) {
      setThemeState(DEFAULT_THEME);
    }
  }, [available, theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = useCallback(
    (id) => {
      if (!available.some((t) => t.id === id)) return;
      localStorage.setItem(STORAGE_KEY, id);
      setThemeState(id);
    },
    [available]
  );

  const value = useMemo(
    () => ({ theme, setTheme, themes: THEMES, available, disabledThemeIds, toggleThemeAvailability }),
    [theme, setTheme, available, disabledThemeIds, toggleThemeAvailability]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function useChartColors() {
  const { theme } = useTheme();
  return (THEME_BY_ID[theme] ?? THEME_BY_ID[DEFAULT_THEME]).chart;
}
